import { doc } from 'firebase/firestore'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore'
import {
  SubmitHandler,
  useController,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { Required } from '../../../assets/Required'
import { CategorySelector } from '../../../assets/category/Selector'
import { usePopupMessage } from '../../../assets/messenger'
import { SpinnerButton } from '../../../assets/spinners'
import {
  Article,
  ArticleDraft,
  Authorization,
  articleRef,
  articlesRef,
} from '../../../firebase'
import { useAuthState } from '../../../firebase/hooks'
import { useTags } from '../../../hooks/models/useTags'
import {
  BlockedUrlError,
  PublicationParams,
  createArticleByDraft,
  updateArticleByDraft,
} from '../../../models/article'
import { logEvent } from '../../../utils'
import { LimitationType } from '../LimitedAuthorizationSetting'
import { VisibilitySettingFormGroup } from '../VisibilitySettingFormGroup'

type PublicationFormFields = {
  youtube_url: string
  category_id: string
  type: Article['type']
  tags: string[]
  visibility: Authorization
  visible_uids: { value: string }[]
  limitation_type: LimitationType
  is_beginner: boolean
}

type Props = {
  draft: ArticleDraft
}

export const ArticleDraftPublicationForm = ({ draft }: Props) => {
  const router = useRouter()
  const { setPopupMessage } = usePopupMessage()
  const { user } = useAuthState()

  const prepareArticleRef =
    draft.publication_id === undefined
      ? doc(articlesRef)
      : articleRef(draft.publication_id)

  const [article] = useDocumentDataOnce(
    draft.publication_id !== undefined ? articleRef(draft.publication_id) : null
  )
  const { tags } = useTags(article?.tag_ids)

  const {
    reset,
    register,
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<PublicationFormFields>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const sharedPath = `/articles/${prepareArticleRef.id}`
  const { field: visibilityField } = useController({
    control,
    name: 'visibility',
  })
  const { field: limitationTypeField } = useController({
    control,
    name: 'limitation_type',
  })
  const visibleUidsFieldArray = useFieldArray({
    control,
    name: 'visible_uids',
  })

  const [collaboratorUids, setCollaboratorUids] = useState(
    article?.collaborator_uids ?? []
  )
  useEffect(() => setCollaboratorUids(draft.collaborator_uids), [draft])

  useEffect(() => {
    reset(
      article
        ? {
            type: article.type,
            tags: tags ? tags.map((tag) => tag.name) : [],
            category_id: article.category_id,
            youtube_url:
              article.youtube_code !== undefined
                ? `https://www.youtube.com/watch?v=${article.youtube_code}`
                : '',
            visibility: article.visibility,
            visible_uids: article.limited_visibility_details
              ? article.limited_visibility_details.authorized_uids.map(
                  (uid) => {
                    return {
                      value: uid,
                    }
                  }
                )
              : [],
            is_beginner: article.is_beginner,
            limitation_type:
              article.limited_visibility_details?.authorized_by_url === true
                ? 'limited_by_url'
                : 'limited_by_uids',
          }
        : {
            category_id: undefined,
            tags: [],
            youtube_url: '',
            type: 'explanation',
            visibility: 'public',
            limitation_type: 'limited_by_uids',
            visible_uids: [],
          }
    )
  }, [article, tags])

  const {
    field: { onChange: handleCategoryIdChange, value: categoryId },
  } = useController({
    name: 'category_id',
    control,
    rules: { required: true },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const onSubmit: SubmitHandler<PublicationFormFields> = async ({
    youtube_url,
    limitation_type,
    visible_uids,
    tags,
    ...fields
  }) => {
    setIsSubmitting(true)

    const youtubeCode = youtube_url.split(/[/=]/g).slice(-1)[0]
    const params: PublicationParams = {
      ...fields,
      tags: tags.filter((t) => t !== ''),
      youtube_code: youtubeCode || undefined,
      limited_visibility_details: {
        authorized_by_url: limitation_type === 'limited_by_url',
        authorized_uids: visible_uids.map((u) => u.value),
      },
      collaborator_uids: collaboratorUids,
    }
    try {
      const publicationRef =
        draft.publication_id !== undefined
          ? await updateArticleByDraft(draft, params)
          : await createArticleByDraft(draft, params, prepareArticleRef)
      await router.push(`/articles/${publicationRef.id}?share_modal=open`)
      setPopupMessage('記事を投稿しました。')
      logEvent(
        draft.publication_id !== undefined
          ? 'update_article'
          : 'create_article',
        {
          uid: user?.uid,
          id: publicationRef.id,
        }
      )
    } catch (error) {
      let message: string
      if (error instanceof Error) {
        setPopupMessage(error.message, 'danger')
        message = error.message
      } else {
        message = 'unexpected.'
        setPopupMessage('予期せぬエラーが発生しました。', 'danger')
      }
      logEvent(
        draft.publication_id !== undefined
          ? 'failed_update_article'
          : 'failed_create_article',
        {
          message,
          // 画面には出さないドメインを、誤検知の調査用に記録する（GAのパラメータ上限100文字）
          ...(error instanceof BlockedUrlError
            ? { blocked_hosts: error.blockedHosts.join(',').slice(0, 100) }
            : {}),
          uid: user?.uid,
          draft_id: draft.id,
          id: draft.publication_id,
        }
      )
      setIsSubmitting(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <h2 className="h6 fw-bold">
        記事のカテゴリと種類
        <Required />
      </h2>
      <Form.Group className="mb-2">
        <InputGroup>
          <CategorySelector
            value={categoryId}
            onChange={handleCategoryIdChange}
          />
          <Form.Select
            {...register('type', {
              required: true,
            })}
          >
            <option value="explanation">解説</option>
            <option value="problem">問題</option>
            <option value="discussion">議論</option>
          </Form.Select>
        </InputGroup>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Check
          label="初心者として投稿する"
          id="beginner-publish"
          {...register('is_beginner')}
        />
        <p className="text-muted small">
          初心者として投稿すると、間違いや考慮が足りていない点が含まれている可能性が高いことが冒頭で閲覧者に表示されます。
        </p>
      </Form.Group>
      <VisibilitySettingFormGroup
        sharedPath={sharedPath}
        visibilityField={visibilityField}
        visibleUidsFieldArray={visibleUidsFieldArray}
        limitationTypeField={limitationTypeField}
      />

      <h2 className="h6 fw-bold">タグ</h2>
      <Form.Group className="mb-4">
        <InputGroup>
          {[0, 1, 2].map((index: number) => (
            <Form.Control
              key={index}
              {...register(`tags.${index}`)}
              placeholder={`${index + 1}つ目のタグ`}
            />
          ))}
        </InputGroup>
      </Form.Group>
      <h2 className="h6 fw-bold">YouTubeのURL</h2>
      <Form.Group className="mb-4">
        <Form.Control
          placeholder="https://www.youtube.com/watch?v=[ID]"
          {...register('youtube_url')}
        />
      </Form.Group>
      <div className="d-flex gap-4">
        <Link href={`/article_drafts/${draft.id}/edit`}>
          <a className="btn btn-secondary">編集に戻る</a>
        </Link>
        <SpinnerButton
          loading={isSubmitting}
          loadingText="投稿中..."
          className="d-inline-flex align-items-center"
          disabled={!isValid || draft.title === '' || draft.body === ''}
        >
          投稿
        </SpinnerButton>
      </div>
    </Form>
  )
}
